import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, BadgeCheck, Loader2 } from "lucide-react";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import useProjectEnqStore from "../../../store/customer/useProjectEnqStore";

const ProjectInviteForm = () => {
  const navigate = useNavigate();
  const {
    projects,
    isLoading: projectsLoading,
    fetchProjects,
  } = useProjectStatus();
  const { updateProjectStatus, isLoading: statusLoading } =
    useProjectEnqStore();
  const [pendingProjects, setPendingProjects] = useState([]);


  useEffect(() => {
    fetchProjects("pending_approval");  
  }, [fetchProjects]);

  useEffect(() => {
    setPendingProjects(projects);
  }, [projects]);

  const handleAccept = async (project) => {
    try {
      const response = await updateProjectStatus(project.id, "accept");
      if (response.success) {
        navigate("/customer/livecard");
      }
    } catch (error) {
      console.error("Error accepting project:", error);
    }
  };

  const handleDecline = async (project) => {
    try {
      const response = await updateProjectStatus(project.id, "decline");
      if (response.success) {
        navigate("/customer/rejectcard");
      }
    } catch (error) {
      console.error("Error declining project:", error);
    }
  };

  const isLoading = projectsLoading || statusLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!pendingProjects || pendingProjects.length === 0) {
    return (
      <div className="text-center text-gray-600 p-4">
        No pending project invitations available at this time.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-4 sm:gap-16">
        {pendingProjects.map((project, index) => (
          <div key={project.id} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            {/* Company Image */}
            <div className="flex justify-center mb-2 sm:mb-4">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg overflow-hidden">
                <img
                  src="/assets/DUCKTAIL-HOMELOAN.aviF"
                  alt="Company Building"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-lg sm:text-xl font-bold text-center mb-1 sm:mb-2">
              {project?.builder_company || "N/A"}
            </h1>

            {/* Subtitle */}
            <p className="text-xs sm:text-sm text-gray-600 text-center mb-2 sm:mb-4">
              Project Name : {project?.project_name || "N/A"}
            </p>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
              {/* Company ID */}
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <BadgeCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-500">
                    Company Ducktail ID
                  </div>
                  <div className="text-xs sm:text-sm font-medium">
                    {project?.builder_ducktail_id || "N/A"}
                  </div>
                </div>
              </div>

              {/* Phone Number */}
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                <div>
                  <div className="text-[10px] sm:text-xs text-gray-500">Phone Number</div>
                  <div className="text-xs sm:text-sm font-medium">
                    {project?.builder_contact || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-center gap-6 sm:gap-12">
              <button
                onClick={() => handleDecline(project)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-500 text-white rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-red-600 transition"
              >
                DECLINE
              </button>
              <button
                onClick={() => handleAccept(project)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-500 text-white rounded-md sm:rounded-lg text-xs sm:text-sm font-medium hover:bg-green-600 transition"
              >
                ACCEPT
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Floating Support Button */}
      <button
        className="fixed bottom-3 right-3 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
        onClick={() => navigate("/customer/help")}
      >
        <img src="/assets/Support.png" alt="Icon" className="w-7 h-7 sm:w-9 sm:h-9" />
      </button>
    </div>
  );
};

export default ProjectInviteForm;
